import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import '../styles/global.css';

export default function SessionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invites, setInvites] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');

  useEffect(() => {
    api.get(`/sessions/${id}`)
      .then(res => { setSession(res.data); setLoading(false); })
      .catch(() => { setLoading(false); navigate('/'); });
    api.get(`/sessions/${id}/invites`)
      .then(res => setInvites(res.data))
      .catch(() => {});
  }, [id, navigate]);

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/sessions/${id}/invite`, { email: inviteEmail });
      setInviteSuccess(`Invite sent to ${inviteEmail}`);
      setInviteEmail('');
      const res = await api.get(`/sessions/${id}/invites`);
      setInvites(res.data);
    } catch { setInviteSuccess('Failed to send invite. Please try again.'); }
  };

  const removeInvite = async (inviteId) => {
    await api.delete(`/sessions/${id}/invites/${inviteId}`);
    setInvites(invites.filter(i => i.id !== inviteId));
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

  const videos = session?.files?.filter(f => f.file_type?.startsWith('video')) || [];
  const images = session?.files?.filter(f => f.file_type?.startsWith('image')) || [];

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-soft)' }}>Loading...</div>;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header className="app-header">
        <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '20px', color: 'var(--teal)' }}>
          BUMP <span style={{ color: '#F68F7C' }}>&#x2665;&#xFE0E;</span> BABY IMAGING
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '100px', padding: '6px 14px', fontSize: '12px', fontWeight: 700, color: 'var(--text-soft)', cursor: 'pointer', fontFamily: 'Nunito, sans-serif' }}>Log out</button>
        </div>
      </header>

      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '24px', width: '100%', flex: 1 }}>
        {/* Back */}
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          ← My Sessions
        </button>

        {/* Detail Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '24px', color: 'var(--teal-dark)' }}>{session?.session_type}</div>
            <div style={{ fontSize: '13px', color: 'var(--text-soft)', marginTop: '4px' }}>
              Session on {formatDate(session?.session_date)}
              {session?.expires_at && <span style={{ color: 'var(--coral)', marginLeft: '8px' }}>· Available until {formatDate(session?.expires_at)}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button onClick={() => setShowInviteModal(true)} style={{ background: 'white', color: 'var(--teal)', border: '2px solid var(--border)', borderRadius: '100px', padding: '9px 18px', fontSize: '13px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer' }}>
              Invite Family
            </button>
            <a href={`https://zippy-flexibility-production.up.railway.app/sessions/${id}/download`} style={{ background: 'var(--teal)', color: 'white', border: 'none', borderRadius: '100px', padding: '9px 18px', fontSize: '13px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
              Download All
            </a>
          </div>
        </div>

        {/* Video Section */}
        {videos.length > 0 && (
          <>
            <div className="section-label">Video Session</div>
            <div style={{ marginBottom: '32px', maxWidth: '520px' }}>
              {videos.map(video => (
                <div key={video.id} style={{ background: 'white', borderRadius: '14px', overflow: 'hidden', border: '1px solid var(--border)', boxShadow: '0 2px 10px rgba(83,147,144,0.08)' }}>
                  <div style={{ background: 'linear-gradient(135deg, var(--teal-dark), var(--teal))', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative' }}
                    onClick={() => window.open(video.drive_view_link, '_blank')}>
                    <div style={{ width: '52px', height: '52px', background: 'rgba(255,255,255,0.95)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: 'var(--teal)' }}>▶</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--teal-dark)', fontFamily: 'Nunito, sans-serif' }}>Video Session</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-soft)' }}>MP4 · {video.file_size ? `${Math.round(video.file_size / 1024 / 1024)} MB` : ''}</div>
                    </div>
                    <a href={video.drive_download_link} target="_blank" rel="noreferrer" style={{ width: '30px', height: '30px', background: 'var(--teal-light)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--teal)', fontSize: '13px', textDecoration: 'none' }}>↓</a>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Images Section */}
        {images.length > 0 && (
          <>
            <div className="section-label">Images</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px', marginBottom: '32px' }}>
              {images.map((image, i) => {
                const gradients = ['linear-gradient(135deg, var(--teal-light), var(--teal))', 'linear-gradient(135deg, var(--coral-light), var(--coral))', 'linear-gradient(135deg, var(--teal-light), var(--teal-dark))', 'linear-gradient(135deg, var(--coral-light), var(--coral-dark))'];
                return (
                  <div key={image.id} style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--border)', cursor: 'pointer', transition: 'transform 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
                    <div style={{ aspectRatio: '1', background: gradients[i % 4], display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}
                      onClick={() => window.open(image.drive_view_link, '_blank')}>
                      <img src={image.drive_view_link} alt={image.file_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                    <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-soft)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{image.file_name}</span>
                      <a href={image.drive_download_link} target="_blank" rel="noreferrer" style={{ width: '26px', height: '26px', background: 'var(--teal-light)', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--teal)', fontSize: '11px', textDecoration: 'none', flexShrink: 0 }}>↓</a>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(45,100,100,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div className="card" style={{ maxWidth: '440px', width: '100%' }}>
            <h3 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '20px', color: 'var(--teal-dark)', marginBottom: '6px' }}>Invite Family and Friends</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-soft)', marginBottom: '20px', lineHeight: '1.5' }}>
              Enter their email address and they will receive a link to view this gallery. They won't be able to see your other sessions.
            </p>
            {inviteSuccess && <p style={{ fontSize: '13px', color: 'var(--teal-dark)', background: 'var(--teal-light)', padding: '10px 14px', borderRadius: '8px', marginBottom: '12px' }}>{inviteSuccess}</p>}
            <form onSubmit={handleInvite} style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
              <input type="email" placeholder="grandma@email.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} required style={{ flex: 1, padding: '11px 14px', border: '2px solid var(--border)', borderRadius: '10px', fontSize: '14px', color: 'var(--text)', outline: 'none' }} />
              <button type="submit" style={{ background: 'var(--teal)', color: 'white', border: 'none', borderRadius: '10px', padding: '11px 18px', fontSize: '13px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer', whiteSpace: 'nowrap' }}>Send Invite</button>
            </form>
            {invites.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                {invites.map(invite => (
                  <div key={invite.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--teal-light)', borderRadius: '10px', padding: '10px 14px' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--teal-dark)' }}>{invite.email}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-soft)' }}>{invite.status === 'accepted' ? 'Accepted' : 'Pending'}</div>
                    </div>
                    <button onClick={() => removeInvite(invite.id)} style={{ background: 'none', border: 'none', color: 'var(--coral)', cursor: 'pointer', fontSize: '16px' }}>×</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowInviteModal(false)} style={{ background: 'var(--teal-light)', color: 'var(--teal)', border: 'none', borderRadius: '10px', padding: '11px 22px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer' }}>Done</button>
            </div>
          </div>
        </div>
      )}

      <footer className="app-footer">
        <p>Made with <span className="heart">&#x2665;&#xFE0E;</span> by <strong>Bump Baby Imaging</strong></p>
        <p style={{ marginTop: '4px' }}>info@bumpbabyimaging.com.au</p>
      </footer>
    </div>
  );
}
